import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Trash2, AlertTriangle, LogOut, UserPlus, ImagePlus } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, API } from "../lib/api";

const TABS = [["stories", "Stories"], ["pledges", "Pledges"], ["enquiries", "Enquiries"], ["feed", "Feed"], ["team", "Team"]];

export default function Admin() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stories");
  const [stories, setStories] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [team, setTeam] = useState([]);
  const [feed, setFeed] = useState([]);
  const [feedForm, setFeedForm] = useState({ caption: "", link: "" });
  const [feedFile, setFeedFile] = useState(null);
  const feedFileRef = useRef(null);
  const [igStatus, setIgStatus] = useState(null);
  const [igToken, setIgToken] = useState("");
  const [memberForm, setMemberForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, p, e, t, f, ig] = await Promise.all([
        api.get("/admin/stories"), api.get("/admin/pledges"), api.get("/admin/enquiries"), api.get("/admin/team"), api.get("/feed"), api.get("/admin/instagram/status"),
      ]);
      setStories(s.data); setPledges(p.data); setEnquiries(e.data); setTeam(t.data); setFeed(f.data); setIgStatus(ig.data);
    } catch {}
  };
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  if (!checked) return <div className="min-h-screen bg-[#090A0F] text-slate-500 flex items-center justify-center text-sm">Checking access…</div>;
  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#090A0F] flex flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-slate-300 font-grotesk">Admins only. Please sign in with the campaign account.</p>
        <Link to="/login" data-testid="admin-login-link" className="bg-[#EF4444] text-white font-bold rounded-full px-6 py-2.5 text-sm">Go to sign in</Link>
      </div>
    );
  }

  const approve = async (id) => { await api.post(`/admin/stories/${id}/approve`); toast.success("Story published."); load(); };
  const removeStory = async (id) => { await api.delete(`/admin/stories/${id}`); toast.success("Story removed."); load(); };
  const removePledge = async (id) => { await api.delete(`/admin/pledges/${id}`); toast.success("Pledge removed."); load(); };
  const removeMember = async (uid) => {
    try { await api.delete(`/admin/team/${uid}`); toast.success("Team member removed."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addMember = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/team", memberForm);
      toast.success(`${memberForm.name} can now sign in to the tracker.`);
      setMemberForm({ name: "", email: "", password: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addFeedPost = async (e) => {
    e.preventDefault();
    if (!feedFile) { toast.error("Choose an image first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("image", feedFile);
      fd.append("caption", feedForm.caption);
      fd.append("link", feedForm.link);
      await api.post("/admin/feed", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Posted to the movement feed.");
      setFeedForm({ caption: "", link: "" });
      setFeedFile(null);
      if (feedFileRef.current) feedFileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeFeedPost = async (id) => { await api.delete(`/admin/feed/${id}`); toast.success("Post removed."); load(); };
  const connectIg = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/instagram/connect", { access_token: igToken });
      toast.success(`Instagram connected: @${data.username} — ${data.synced} posts synced.`);
      setIgToken("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const syncIg = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/instagram/sync");
      toast.success(data.synced ? `${data.synced} new post(s) pulled in.` : "Feed already up to date.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disconnectIg = async () => {
    await api.delete("/admin/instagram");
    toast.success("Instagram disconnected — synced posts stay on the site.");
    load();
  };

  const pending = stories.filter((s) => !s.approved);

  return (
    <div className="min-h-screen bg-[#090A0F] text-white" data-testid="admin-dashboard">
      <header className="border-b border-[#212636] px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-3"><Logo size={28} /><span className="font-display font-extrabold uppercase text-sm">Campaign admin</span></Link>
        <div className="flex items-center gap-4">
          <Link to="/tracker" data-testid="admin-tracker-link" className="text-xs text-[#10B981] font-bold hover:underline">Enquiry tracker</Link>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout-button" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white"><LogOut size={13} /> Log out</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="bg-[#12141C] border border-[#212636] rounded-xl p-4"><p className="mono-label text-slate-500">Pending stories</p><p className="font-display font-extrabold text-3xl mt-1 text-[#F59E0B]" data-testid="admin-pending-count">{pending.length}</p></div>
          <div className="bg-[#12141C] border border-[#212636] rounded-xl p-4"><p className="mono-label text-slate-500">Pledges</p><p className="font-display font-extrabold text-3xl mt-1">{pledges.length}</p></div>
          <div className="bg-[#12141C] border border-[#212636] rounded-xl p-4"><p className="mono-label text-slate-500">Enquiries</p><p className="font-display font-extrabold text-3xl mt-1">{enquiries.length}</p></div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${tab === key ? "bg-[#EF4444] text-white" : "border border-[#212636] text-slate-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "stories" && (
          <div className="space-y-3">
            {stories.length === 0 && <p className="text-sm text-slate-500">No submissions yet.</p>}
            {stories.map((s) => (
              <div key={s.id} data-testid={`admin-story-${s.id}`} className="bg-[#12141C] border border-[#212636] rounded-xl p-5">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h3 className="font-display font-bold text-sm">{s.title}</h3>
                  <span className="mono-label text-slate-500 text-[9px]">{s.name} · {s.role} · {s.theme}</span>
                  {s.urgent && <span className="flex items-center gap-1 bg-[#B91C1C] text-white text-[10px] font-bold rounded-full px-2 py-0.5"><AlertTriangle size={10} /> Priority review</span>}
                  {!s.ai_classified && <span className="bg-slate-700 text-slate-300 text-[10px] rounded-full px-2 py-0.5">manual triage needed</span>}
                  {s.approved && <span className="bg-[#1E4B3E] text-[#F5F2EC] text-[10px] font-bold rounded-full px-2 py-0.5">Published</span>}
                </div>
                <p className="text-xs text-slate-400 leading-relaxed whitespace-pre-line mb-3">{s.story}</p>
                {s.attachment_path && (
                  <a href={`${API}/files/${s.attachment_path}`} target="_blank" rel="noreferrer" className="text-xs text-[#F59E0B] underline block mb-3">
                    Attachment: {s.attachment_name || "file"} ({s.attachment_type})
                  </a>
                )}
                <div className="flex gap-2">
                  {!s.approved && (
                    <button onClick={() => approve(s.id)} data-testid={`admin-approve-${s.id}`} className="flex items-center gap-1.5 bg-[#1E4B3E] hover:bg-[#16382e] text-white text-xs font-bold rounded-lg px-3 py-2"><Check size={13} /> Approve &amp; publish</button>
                  )}
                  <button onClick={() => removeStory(s.id)} data-testid={`admin-delete-${s.id}`} className="flex items-center gap-1.5 border border-[#212636] hover:border-[#EF4444] hover:text-[#EF4444] text-slate-400 text-xs font-bold rounded-lg px-3 py-2 transition-colors"><Trash2 size={13} /> Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "pledges" && (
          <div className="space-y-2">
            {pledges.length === 0 && <p className="text-sm text-slate-500">No pledges yet.</p>}
            {pledges.map((p) => (
              <div key={p.id} className="bg-[#12141C] border border-[#212636] rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <span className="text-sm font-bold">{p.name}</span>
                  <span className="mono-label text-slate-500 text-[9px] ml-3">{p.region}</span>
                  {p.comment && <p className="text-xs text-slate-400 mt-1">{p.comment}</p>}
                </div>
                <button onClick={() => removePledge(p.id)} data-testid={`admin-pledge-delete-${p.id}`} className="text-slate-500 hover:text-[#EF4444] transition-colors" aria-label="Remove pledge"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-2">
            {enquiries.length === 0 && <p className="text-sm text-slate-500">No partnership enquiries yet.</p>}
            {enquiries.map((e) => (
              <div key={e.id} className="bg-[#12141C] border border-[#212636] rounded-xl px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold">{e.org}</span>
                  <a href={`mailto:${e.email}`} className="text-xs text-[#F59E0B] underline">{e.email}</a>
                  {e.tier && <span className="mono-label text-slate-500 text-[9px]">{e.tier}</span>}
                </div>
                {e.message && <p className="text-xs text-slate-400 mt-1">{e.message}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "feed" && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="admin-feed-tab">
            <div className="bg-[#12141C] border border-[#212636] rounded-xl p-6 mb-6 md:col-span-2" data-testid="instagram-connect-card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className="font-display font-bold text-sm">Instagram auto-sync</h3>
                {igStatus?.connected ? (
                  <span className="mono-label text-[10px] text-[#10B981]">Connected · @{igStatus.username}{igStatus.last_sync ? ` · last sync ${igStatus.last_sync.slice(0, 10)}` : ""}</span>
                ) : (
                  <span className="mono-label text-[10px] text-slate-500">Not connected</span>
                )}
              </div>
              {igStatus?.connected ? (
                <div className="flex gap-2">
                  <button onClick={syncIg} disabled={busy} data-testid="ig-sync-button" className="bg-[#1E4B3E] hover:bg-[#16382e] disabled:opacity-50 text-white font-bold rounded-lg px-4 py-2 text-xs transition-colors">Sync now</button>
                  <button onClick={disconnectIg} data-testid="ig-disconnect-button" className="border border-[#212636] hover:border-[#EF4444] hover:text-[#EF4444] text-slate-400 font-bold rounded-lg px-4 py-2 text-xs transition-colors">Disconnect</button>
                </div>
              ) : (
                <form onSubmit={connectIg} className="space-y-3">
                  <p className="text-[11px] text-slate-500 leading-relaxed">One-time setup: switch @movementbreakthecycle to a <strong>professional/business</strong> account (Instagram → Edit profile → Switch to professional account), then create a long-lived token via Meta for Developers (Instagram Login, instagram_business_basic). Paste the token here — it's stored encrypted and auto-refreshes. New posts then appear on the site by themselves, checked every 30 minutes.</p>
                  <div className="flex gap-2">
                    <input required type="password" value={igToken} onChange={(e) => setIgToken(e.target.value)} placeholder="Long-lived Instagram access token" data-testid="ig-token-input"
                      className="flex-1 bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#10B981]" />
                    <button type="submit" disabled={busy} data-testid="ig-connect-button" className="bg-[#1E4B3E] hover:bg-[#16382e] disabled:opacity-50 text-white font-bold rounded-lg px-4 py-2.5 text-xs transition-colors">Connect</button>
                  </div>
                </form>
              )}
            </div>
            <form onSubmit={addFeedPost} className="bg-[#12141C] border border-[#212636] rounded-xl p-6 h-fit" data-testid="admin-feed-form">
              <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-1"><ImagePlus size={15} className="text-[#10B981]" /> Post to the movement feed</h3>
              <p className="text-[11px] text-slate-500 mb-4">Shows in "Latest from the movement" on the site. Screenshot or save your Instagram post and drop it here.</p>
              <div className="space-y-3">
                <input ref={feedFileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFeedFile(e.target.files[0] || null)}
                  data-testid="feed-input-image"
                  className="w-full text-xs text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-[#1E4B3E] file:text-white file:px-3 file:py-2 file:text-xs file:font-bold" />
                <input value={feedForm.caption} onChange={(e) => setFeedForm({ ...feedForm, caption: e.target.value })} placeholder="Caption (optional)" maxLength={300} data-testid="feed-input-caption"
                  className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#10B981]" />
                <input value={feedForm.link} onChange={(e) => setFeedForm({ ...feedForm, link: e.target.value })} placeholder="Link to the Instagram post (optional)" maxLength={300} data-testid="feed-input-link"
                  className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#10B981]" />
                <button type="submit" disabled={busy} data-testid="feed-submit-button" className="w-full bg-[#1E4B3E] hover:bg-[#16382e] disabled:opacity-50 text-white font-bold rounded-lg py-2.5 text-sm transition-colors">
                  {busy ? "Posting…" : "Post to feed"}
                </button>
              </div>
            </form>
            <div className="grid grid-cols-2 gap-3">
              {feed.length === 0 && <p className="text-sm text-slate-500 col-span-2">No posts yet — your first one will appear on the site's Media section.</p>}
              {feed.map((p) => (
                <div key={p.id} className="relative group rounded-xl overflow-hidden border border-[#212636] aspect-square" data-testid={`admin-feed-${p.id}`}>
                  <img src={`${API}/feed/image/${p.id}`} alt={p.caption || "Feed post"} className="w-full h-full object-cover" />
                  <button onClick={() => removeFeedPost(p.id)} data-testid={`admin-feed-delete-${p.id}`}
                    className="absolute top-2 right-2 bg-black/70 hover:bg-[#EF4444] text-white rounded-lg p-1.5 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove post">
                    <Trash2 size={13} />
                  </button>
                  {p.caption && <p className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] text-slate-300 px-2 py-1.5 line-clamp-1">{p.caption}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "team" && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="admin-team-tab">
            <div className="space-y-2">
              <p className="text-xs text-slate-500 mb-3">Admins and team members can use the enquiry tracker. Only admins see this dashboard.</p>
              {team.map((u) => (
                <div key={u.user_id} className="bg-[#12141C] border border-[#212636] rounded-xl px-4 py-3 flex items-center justify-between gap-3" data-testid={`admin-team-${u.user_id}`}>
                  <div>
                    <span className="text-sm font-bold">{u.name}</span>
                    <span className={`mono-label text-[9px] ml-3 ${u.role === "admin" ? "text-[#F59E0B]" : "text-[#10B981]"}`}>{u.role}</span>
                    <p className="text-xs text-slate-500">{u.email} · {u.auth_provider}</p>
                  </div>
                  {u.role !== "admin" && u.user_id !== user.user_id && (
                    <button onClick={() => removeMember(u.user_id)} data-testid={`admin-team-remove-${u.user_id}`} className="text-slate-500 hover:text-[#EF4444] transition-colors" aria-label="Remove member"><Trash2 size={15} /></button>
                  )}
                </div>
              ))}
            </div>
            <form onSubmit={addMember} className="bg-[#12141C] border border-[#212636] rounded-xl p-6 h-fit" data-testid="admin-team-form">
              <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-4"><UserPlus size={15} className="text-[#10B981]" /> Add a team member</h3>
              <div className="space-y-3">
                <input required value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} placeholder="First name" maxLength={80} data-testid="team-input-name"
                  className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#10B981]" />
                <input required type="email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} placeholder="Their email" data-testid="team-input-email"
                  className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#10B981]" />
                <input required type="text" minLength={8} value={memberForm.password} onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })} placeholder="Temporary password (8+ chars) — share it in person" data-testid="team-input-password"
                  className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#10B981]" />
                <button type="submit" disabled={busy} data-testid="team-add-button" className="w-full bg-[#1E4B3E] hover:bg-[#16382e] disabled:opacity-50 text-white font-bold rounded-lg py-2.5 text-sm transition-colors">
                  {busy ? "Adding…" : "Add to the team"}
                </button>
                <p className="text-[11px] text-slate-500">They sign in at /login and land on the enquiry tracker. No passwords are ever emailed — share it face to face.</p>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
